/**
 * Black Matte VTC - Partner Invoice Detail
 * Pure view layer - ALL data from API
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { partnerService } from '../../services/partnerService';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Separator } from '../../components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  ArrowLeft,
  FileText,
  Calendar,
  AlertTriangle,
  CreditCard,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download,
  Building2
} from 'lucide-react';

// Invoice status badge
const InvoiceStatusBadge = ({ status }) => {
  const statusConfig = {
    draft: { label: 'Draft', className: 'bg-neutral-500/10 text-neutral-400 border-neutral-500/20', icon: Clock },
    issued: { label: 'Issued', className: 'bg-blue-500/10 text-blue-400 border-blue-500/20', icon: FileText },
    sent: { label: 'Sent', className: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: AlertCircle },
    paid: { label: 'Paid', className: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', icon: CheckCircle },
    partially_paid: { label: 'Partially Paid', className: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: AlertCircle },
    overdue: { label: 'Overdue', className: 'bg-red-500/10 text-red-400 border-red-500/20', icon: AlertTriangle },
    cancelled: { label: 'Cancelled', className: 'bg-neutral-500/10 text-neutral-400 border-neutral-500/20', icon: XCircle },
  };
  
  const config = statusConfig[status] || statusConfig.draft;
  const Icon = config.icon;
  
  return (
    <Badge variant="outline" className={`flex items-center gap-1 text-sm px-3 py-1 ${config.className}`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

const PartnerInvoiceDetail = () => {
  const { invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const data = await partnerService.getInvoice(invoiceId);
        setInvoice(data);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoice();
  }, [invoiceId]);

  if (loading) {
    return <InvoiceDetailSkeleton />;
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" asChild className="text-neutral-400 hover:text-white">
          <Link to="/partner/invoices">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Invoices
          </Link>
        </Button>
        
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-400 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()} variant="outline">
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const isOverdue = invoice.status === 'overdue';
  const hasDue = invoice.amount_due > 0;

  return (
    <div className="space-y-6" data-testid="partner-invoice-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" asChild className="text-neutral-400 hover:text-white">
            <Link to="/partner/invoices">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-white">
                Invoice {invoice.invoice_number}
              </h1>
              <InvoiceStatusBadge status={invoice.status} />
            </div>
            <p className="text-neutral-500">
              Issued {invoice.issued_at ? new Date(invoice.issued_at).toLocaleDateString() : '—'}
            </p>
          </div>
        </div>

        {hasDue && (
          <Button 
            asChild
            className="bg-white text-black hover:bg-neutral-200"
          >
            <Link to="/partner/payments">
              <CreditCard className="h-4 w-4 mr-2" />
              Make Payment
            </Link>
          </Button>
        )}
      </div>

      {/* Overdue Warning */}
      {isOverdue && (
        <div 
          className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3"
          data-testid="overdue-warning"
        >
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-400">This invoice is overdue</p>
            <p className="text-xs text-red-400/80 mt-1">
              Please settle this invoice immediately to avoid service interruption.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Invoice Details */}
          <Card className="bg-neutral-900 border-neutral-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Invoice Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Invoice Number</p>
                  <p className="text-white font-medium">{invoice.invoice_number}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Issue Date</p>
                  <p className="text-white">
                    {invoice.issued_at ? new Date(invoice.issued_at).toLocaleDateString() : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Due Date</p>
                  <p className={`${isOverdue ? 'text-red-400' : 'text-white'}`}>
                    {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Period</p>
                  <p className="text-white text-sm">
                    {invoice.period_start && invoice.period_end 
                      ? `${new Date(invoice.period_start).toLocaleDateString()} - ${new Date(invoice.period_end).toLocaleDateString()}`
                      : '—'
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card className="bg-neutral-900 border-neutral-800">
            <CardHeader>
              <CardTitle className="text-white">Line Items</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {invoice.line_items && invoice.line_items.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="border-neutral-800 hover:bg-transparent">
                      <TableHead className="text-neutral-400">Description</TableHead>
                      <TableHead className="text-neutral-400 text-right">Qty</TableHead>
                      <TableHead className="text-neutral-400 text-right">Unit Price</TableHead>
                      <TableHead className="text-neutral-400 text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoice.line_items.map((item, index) => (
                      <TableRow key={index} className="border-neutral-800">
                        <TableCell className="text-white">{item.description}</TableCell>
                        <TableCell className="text-neutral-300 text-right">{item.quantity || 1}</TableCell>
                        <TableCell className="text-neutral-300 text-right">
                          €{(item.unit_price || item.amount)?.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-white text-right font-medium">
                          €{item.amount?.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="p-6 text-center text-neutral-500">
                  No line items available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          {invoice.notes && (
            <Card className="bg-neutral-900 border-neutral-800">
              <CardHeader>
                <CardTitle className="text-white">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-neutral-300 whitespace-pre-wrap">{invoice.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar - Summary */}
        <div className="space-y-6">
          {/* Amount Summary - ALL from API */}
          <Card className="bg-neutral-900 border-neutral-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Payment Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-400">Subtotal</span>
                  <span className="text-neutral-300">€{invoice.amount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-400">Tax ({(invoice.tax_rate || 0.2) * 100}%)</span>
                  <span className="text-neutral-300">€{invoice.tax_amount?.toFixed(2)}</span>
                </div>
              </div>

              <Separator className="bg-neutral-800" />

              <div className="flex justify-between">
                <span className="text-neutral-400">Total</span>
                <span className="text-xl font-bold text-white" data-testid="total-amount">
                  €{invoice.total_amount?.toFixed(2)}
                </span>
              </div>

              <Separator className="bg-neutral-800" />

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-400">Amount Paid</span>
                  <span className="text-emerald-400">€{invoice.amount_paid?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-400 font-medium">Amount Due</span>
                  <span className={`text-lg font-bold ${invoice.amount_due > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                    €{invoice.amount_due?.toFixed(2)}
                  </span>
                </div>
              </div>

              {hasDue && (
                <>
                  <Separator className="bg-neutral-800" />
                  <Button 
                    asChild
                    className="w-full bg-white text-black hover:bg-neutral-200"
                  >
                    <Link to="/partner/payments">
                      Pay Now
                    </Link>
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Payment Status */}
          {invoice.paid_at && (
            <Card className="bg-neutral-900 border-neutral-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-500" />
                  Payment Received
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-neutral-300">
                  Paid on {new Date(invoice.paid_at).toLocaleDateString()}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

// Loading skeleton
const InvoiceDetailSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center gap-4">
      <Skeleton className="h-10 w-24 bg-neutral-800" />
      <div>
        <Skeleton className="h-8 w-48 bg-neutral-800" />
        <Skeleton className="h-4 w-32 bg-neutral-800 mt-2" />
      </div>
    </div>
    
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Skeleton className="h-32 w-full bg-neutral-800" />
        <Skeleton className="h-64 w-full bg-neutral-800" />
      </div>
      <div>
        <Skeleton className="h-64 w-full bg-neutral-800" />
      </div>
    </div>
  </div>
);

export default PartnerInvoiceDetail;
